import { useState, useEffect } from 'react';
import type { LayoutConfig } from '../utils/types';
import { DEFAULT_LAYOUT_CONFIG, DEFAULT_RESULTS_LAYOUT, DEFAULT_RESULTS_HEADER_SECTIONS, DEFAULT_RESULTS_COLUMNS } from '../utils/types';
import { getApiBase } from '../utils/api';

/**
 * Fetches the widget layout configuration from the public backend endpoint.
 * Falls back to DEFAULT_LAYOUT_CONFIG if the server has no saved layout or
 * if the request fails (e.g. widget embedded on external site with no backend).
 */
const CACHE_KEY_PREFIX = 'botilia_layout_config';

function cacheKey(lang?: string): string {
  return `${CACHE_KEY_PREFIX}_${lang || 'el'}`;
}

/** Last config this browser saw, so the modal mode is known on the very first
 *  render (a Back-button restore must not wait for /api/layout). */
function readCachedLayout(lang?: string): LayoutConfig | null {
  try {
    const raw = localStorage.getItem(cacheKey(lang));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LayoutConfig>;
    if (!parsed || typeof parsed !== 'object' || !parsed.modalMode) return null;
    return { ...DEFAULT_LAYOUT_CONFIG, ...parsed };
  } catch {
    return null;
  }
}

function writeCachedLayout(lang: string | undefined, config: LayoutConfig): void {
  try {
    localStorage.setItem(cacheKey(lang), JSON.stringify(config));
  } catch {
    /* best-effort */
  }
}

export function useLayoutConfig(lang?: string): { layoutConfig: LayoutConfig; isLoading: boolean } {
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>(() => readCachedLayout(lang) ?? DEFAULT_LAYOUT_CONFIG);
  // "Loading" only matters when we have no idea what the mode is.
  const [isLoading, setIsLoading] = useState(() => readCachedLayout(lang) === null);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10_000);
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';

    fetch(`${getApiBase()}/layout${qs}`, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) return; // 404 → use default
        const data = await res.json() as Partial<LayoutConfig>;
        const merged: LayoutConfig = {
          ...DEFAULT_LAYOUT_CONFIG,
          ...data,
          resultsLayout: data.resultsLayout ?? DEFAULT_RESULTS_LAYOUT,
          resultsHeaderSections: data.resultsHeaderSections ?? DEFAULT_RESULTS_HEADER_SECTIONS,
          resultsColumns: data.resultsColumns ?? DEFAULT_RESULTS_COLUMNS,
          resultsColumnCount: data.resultsColumnCount ?? 1,
        };
        setLayoutConfig(merged);
        writeCachedLayout(lang, merged);
      })
      .catch(() => {
        // Network error or abort → silently use default
      })
      .finally(() => {
        clearTimeout(timer);
        setIsLoading(false);
      });

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [lang]);

  return { layoutConfig, isLoading };
}
